import {
  TableHead,
  Table,
  TableBody,
  TableContainer,
  TableRow,
} from '@mui/material';
import { useState } from 'react';

import {
  HivePaymentData,
  SortDirType,
  SortType,
} from '../../../../api/financial/types';
import { Constants } from '../../../../constants/finance';
import { formatDate, formatOnlyDate, formatReal } from '../../../../helpers';
import PaymentDialog from '../PaymentDialog';
import { PaymentStatusBadge } from '../PaymentStatusBadge';
import { PaymentActionButton } from './PaymentActionButton';
import { SortIndicator } from './SortIndicator';
import {
  Row,
  TableCellHeader,
  StyledCell,
  StyledButton,
  SortableTableCellHeader,
  SortableBox,
} from './styles';

interface TableProps {
  tableData: HivePaymentData[];
  sort: { by: SortType; dir: SortDirType } | null;
  updateData: () => void;
  handleSort: (value: SortType) => void;
}

export default function TableUser({
  tableData,
  sort,
  updateData,
  handleSort,
}: TableProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<
    HivePaymentData | undefined
  >(undefined);

  const handleClickOpen = (payment: HivePaymentData): void => {
    setOpen(true);
    setSelectedPayment(payment);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <SortableTableCellHeader
              align="center"
              onClick={() => handleSort('reference_id')}
            >
              <SortableBox>
                {Constants.paymentNumber}
                <SortIndicator sort={sort} column="reference_id" />
              </SortableBox>
            </SortableTableCellHeader>
            <SortableTableCellHeader
              align="center"
              onClick={() => handleSort('licensed_company_name')}
            >
              <SortableBox>
                {Constants.licensed}
                <SortIndicator sort={sort} column="licensed_company_name" />
              </SortableBox>
            </SortableTableCellHeader>
            <SortableTableCellHeader
              align="center"
              onClick={() => handleSort('public_notice_name')}
            >
              <SortableBox>
                {Constants.notice}
                <SortIndicator sort={sort} column="public_notice_name" />
              </SortableBox>
            </SortableTableCellHeader>
            <SortableTableCellHeader
              align="center"
              onClick={() => handleSort('amount')}
            >
              <SortableBox>
                {Constants.value}
                <SortIndicator sort={sort} column="amount" />
              </SortableBox>
            </SortableTableCellHeader>
            <SortableTableCellHeader
              align="center"
              onClick={() => handleSort('status')}
            >
              <SortableBox>
                {Constants.status}
                <SortIndicator sort={sort} column="status" />
              </SortableBox>
            </SortableTableCellHeader>
            <SortableTableCellHeader
              align="center"
              onClick={() => handleSort('reference_date')}
            >
              <SortableBox>
                {Constants.referencePeriod}
                <SortIndicator sort={sort} column="reference_date" />
              </SortableBox>
            </SortableTableCellHeader>
            <SortableTableCellHeader
              align="center"
              onClick={() => handleSort('finished_at')}
            >
              <SortableBox>
                {Constants.finishDate}
                <SortIndicator sort={sort} column="finished_at" />
              </SortableBox>
            </SortableTableCellHeader>
            <TableCellHeader align="center">
              {Constants.actions}
            </TableCellHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {tableData?.map((payment) => (
            <Row key={payment.id}>
              <StyledCell align="center">
                <StyledButton onClick={() => handleClickOpen(payment)}>
                  {payment.reference_id}
                </StyledButton>
              </StyledCell>
              <StyledCell align="center">
                {payment.licensed_company_name}
              </StyledCell>
              <StyledCell align="center">
                {payment.public_notice_name}
              </StyledCell>
              <StyledCell align="center">
                {formatReal(payment.amount)}
              </StyledCell>
              <StyledCell align="center">
                <PaymentStatusBadge status={payment.status} />
              </StyledCell>
              <StyledCell align="center">
                {formatDate(payment.reference_date)}
              </StyledCell>
              <StyledCell align="center">
                {payment.finished_at && formatOnlyDate(payment.finished_at)}
              </StyledCell>
              <StyledCell align="center">
                <PaymentActionButton
                  status={payment.status}
                  payment={payment}
                  updateData={updateData}
                />
              </StyledCell>
            </Row>
          ))}
        </TableBody>
      </Table>
      {open && selectedPayment && (
        <PaymentDialog
          open={open}
          handleClose={handleClose}
          paymentId={selectedPayment.id}
          updateData={updateData}
        />
      )}
    </TableContainer>
  );
}
